import { Flex } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Breadcrumb, Button, Card, Container } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import { ControlPanelManager } from '../../api/ControlPanelManager';
import { useToasts } from '../../contexts/toasts';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import ScriptEditor from '../../components/common/ScriptEditor';
import AppLoader from '../../components/common/AppLoader';


export default function EnvCustomization() {
  const navigate = useNavigate();
  const [env, setEnv] = useState<string>();
  const [error, setError] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const initEnvironment = useCallback(() => {
    ControlPanelManager.getEnvFile()
      .then(
        ({ data }) => setEnv(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('env-load-error', message, `Environment Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showToast]);

  const reset = useCallback(() => {
    setEnv(undefined);
    initEnvironment();
  }, [initEnvironment]);

  useEffect(reset, [reset]);

  const cancel = useCallback(() => navigate('/control-panel'), [navigate]);

  const handleUpdate = useCallback(() => {
    showLoader();
    const formData = new FormData();
    formData.append("file", new Blob([env!], { type: 'javascript' }));

    ControlPanelManager.uploadEnvFile(formData)
      .then(
        () => {
          hideLoader();
          reset();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('env-update-error', message, `Environment Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [env, showLoader, hideLoader, showToast, reset]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-circle')} />
        <span className="ms-2">An error occurred, try again later.</span>
      </Alert>
    );

    if (!env) return <AppLoader className="py-5" justify="center" size={100} />

    return (
      <Card>
        <Card.Body>
          <Card.Title className="mb-3">Customize Environment</Card.Title>

          <ScriptEditor value={env} onChange={setEnv} language="javascript" height="60vh" />

          <Flex justify="space-between" align="center" className="mt-3">
            <Button variant="light" size="sm" className="border" onClick={cancel}>
              <FontAwesomeIcon icon={solid('chevron-left')} />
              <span className="ms-2">Cancel</span>
            </Button>
            <Button variant="success" size="sm" onClick={handleUpdate}>
              <FontAwesomeIcon icon={solid('save')} />
              <span className="ms-2">Update Environment</span>
            </Button>
          </Flex>
        </Card.Body>
      </Card>
    );
  }, [env, error, handleUpdate, cancel]);

  return (
    <Container className="my-3">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/control-panel" }}>Control Panel</Breadcrumb.Item>
        <Breadcrumb.Item active>Environment Customization</Breadcrumb.Item>
      </Breadcrumb>

      {renderAsyncContent()}
    </Container>
  )
}